
import React from 'react';
import { Search, InfoIcon, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import ErrorDisplay from './ErrorDisplay';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ArticleInputFormProps {
  urlInput: string;
  setUrlInput: React.Dispatch<React.SetStateAction<string>>;
  showContextInput: boolean;
  setShowContextInput: React.Dispatch<React.SetStateAction<boolean>>;
  additionalContext: string;
  setAdditionalContext: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
  progress?: { percentage: number, stage: string } | null;
  handleAnalyze: () => void;
  hasArticlesToRecommend: boolean;
  error: Error | null;
  isErrorDetailsOpen: boolean;
  setIsErrorDetailsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  apiKeyAvailable?: boolean;
}

const ArticleInputForm: React.FC<ArticleInputFormProps> = ({
  urlInput,
  setUrlInput,
  showContextInput,
  setShowContextInput,
  additionalContext,
  setAdditionalContext,
  isLoading,
  progress,
  handleAnalyze,
  hasArticlesToRecommend,
  error,
  isErrorDetailsOpen,
  setIsErrorDetailsOpen,
  apiKeyAvailable = true
}) => {
  return (
    <Card className="mb-6 border-primary/20 shadow-md overflow-hidden neo-card">
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary opacity-70" />
              <Input
                placeholder="Enter URL to find recommended articles..."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                className="pl-10 bg-secondary/70 border-primary/20 focus-visible:ring-primary/30"
                disabled={isLoading}
              />
            </div>
            <Button 
              onClick={handleAnalyze} 
              disabled={!urlInput || isLoading || !hasArticlesToRecommend || !apiKeyAvailable}
              className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-md transition-all duration-200"
            >
              {isLoading ? 'Analyzing...' : 'Find Related'}
            </Button>
          </div>
          
          {isLoading && progress && (
            <div className="space-y-2 animate-fade-in">
              <div className="flex justify-between text-sm">
                <span className="text-primary font-medium">{progress.stage}</span>
                <span className="text-muted-foreground">{Math.round(progress.percentage)}%</span>
              </div>
              <Progress value={progress.percentage} className="h-2 bg-primary/20" indicatorClassName="bg-primary" />
            </div>
          )}
          
          <TooltipProvider>
            <div className="flex items-center space-x-2 p-2 rounded-md bg-secondary/50 border border-primary/10">
              <Switch 
                id="context" 
                checked={showContextInput} 
                onCheckedChange={setShowContextInput}
                disabled={isLoading}
                className="data-[state=checked]:bg-primary"
              />
              <label 
                htmlFor="context" 
                className="text-sm cursor-pointer flex items-center gap-2"
              >
                Add custom filtering instructions
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Add specific instructions like "only show blog articles" or "focus on technical content" to filter and customize your recommendations.</p>
                  </TooltipContent>
                </Tooltip>
              </label>
            </div>
          </TooltipProvider>
          
          {showContextInput && (
            <div className="space-y-2">
              <Textarea
                placeholder="Examples: 'only include blog articles', 'focus on technical guides', 'exclude product pages'..."
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                className="min-h-[100px] bg-secondary/70 border-primary/20 focus-visible:ring-primary/30"
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground px-1">
                Your instructions will be sent directly to the AI to filter and prioritize recommendations.
              </p>
            </div>
          )}
          
          {error && (
            <Collapsible
              open={isErrorDetailsOpen}
              onOpenChange={setIsErrorDetailsOpen}
              className="space-y-2 animate-fade-in"
            >
              <div className="flex items-center justify-between space-x-4 px-4 py-2 bg-destructive/10 border border-destructive/20 rounded-md">
                <h4 className="text-sm font-semibold text-destructive flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Error: {error.message}
                </h4>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-9 p-0 hover:bg-destructive/10">
                    <span className="sr-only">Toggle</span>
                    {isErrorDetailsOpen ? '−' : '+'}
                  </Button>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent className="space-y-2">
                <ErrorDisplay error={error} />
              </CollapsibleContent>
            </Collapsible>
          )}
          
          {!hasArticlesToRecommend && !isLoading && !error && (
            <p className="text-sm text-muted-foreground p-4 border border-yellow-500/20 bg-yellow-500/10 rounded-md flex items-center gap-2">
              <InfoIcon className="h-4 w-4 text-yellow-500" />
              Please crawl a website first to enable article recommendations.
            </p>
          )}
          
          {!apiKeyAvailable && !error && (
            <p className="text-sm text-destructive p-4 border border-destructive/20 bg-destructive/10 rounded-md flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              API key configuration issue. Please contact support.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ArticleInputForm;
